import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";

import type { FlowAnswers } from "@/shared/constants/flowTypes";

export interface TaskRow {
  task_id: string;
  answers: Record<string, unknown> | null;
  status: string;
}

export function useEstateTaskAnswers(
  estateId: number | undefined,
  taskIds: readonly string[],
  queryKeySuffix: string,
) {
  const {
    data: taskRows,
    isLoading,
  } = useQuery({
    queryKey: ["estateTasks", estateId, queryKeySuffix],
    queryFn: async () => {
      if (!estateId) return [];
      const { data, error } = await supabase
        .from("estate_tasks")
        .select("task_id, answers, status")
        .eq("estate_id", estateId)
        .in("task_id", [...taskIds]);
      if (error) throw error;
      return (data ?? []) as TaskRow[];
    },
    enabled: !!estateId,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const initialAnswers = useMemo((): FlowAnswers => {
    const merged: FlowAnswers = {};
    for (const row of taskRows ?? []) {
      if (row.answers && typeof row.answers === "object") {
        for (const [key, value] of Object.entries(row.answers)) {
          if (typeof value === "string" || Array.isArray(value)) {
            merged[key] = value as string | string[];
          }
        }
      }
    }
    return merged;
  }, [taskRows]);

  return { taskRows, initialAnswers, isLoading };
}
